import { useState } from "react";
import { ChevronDown, History } from "lucide-react";
import { fmtDateTime, fmtMelbDay } from "@/lib/date";

const LABELS = {
  achievement: { achieved: "Completed", requalified: "Completed again" },
  qualification: { achieved: "First qualified", requalified: "Requalified" },
};
const day = (e) => (e.effective_at ? fmtMelbDay(e.effective_at) : "date unknown");

function auditLabel(e, kind) {
  if (e.type === "corrected_removed") return "Mistaken entry removed";
  if (e.type === "date_corrected") return `Date corrected: ${e.previous_effective_at ? fmtMelbDay(e.previous_effective_at) : "unknown"} → ${fmtMelbDay(e.effective_at)}`;
  if (e.type === "count_set") return `Total changed ${e.from} → ${e.to}`;
  return `${LABELS[kind]?.[e.type] || e.type} · ${day(e)} (removed as a mistake)`;
}

export default function MilestoneHistory({ record, kind, testid }) {
  const [open, setOpen] = useState(false);
  const events = record?.events || [];
  if (!events.length) return null;
  const voided = new Set(events.filter((e) => e.type === "corrected_removed").map((e) => e.target_event_id));
  const redated = Object.fromEntries(events.filter((e) => e.type === "date_corrected").map((e) => [e.target_event_id, e.effective_at]));
  const real = events.filter((e) => ["achieved", "requalified", "ceased"].includes(e.type) && !voided.has(e.id))
    .map((e) => ({ ...e, effective_at: redated[e.id] ?? e.effective_at }));
  const audit = events.filter((e) => !real.some((r) => r.id === e.id));
  return (
    <div className="mt-2">
      <button onClick={() => setOpen((o) => !o)} data-testid={`${testid}-history-toggle`}
              className="inline-flex items-center gap-1 text-[11px] font-medium text-[#0A1F44]/70 hover:text-[#0A1F44] min-h-[32px]">
        <History className="w-3.5 h-3.5" /> History ({real.length})
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-2 space-y-3">
          <ol className="space-y-2 border-l-2 border-[#C9A961]/40 pl-3" data-testid={`${testid}-history`}>
            {real.map((e) => (
              <li key={e.id} className="text-xs" data-testid={`${testid}-event-${e.type}`}>
                <div className="font-medium text-slate-800">{e.type === "ceased" ? "Qualification ended" : LABELS[kind]?.[e.type] || e.type} · {day(e)}</div>
                {e.note && <div className="text-[11px] text-slate-600 italic">“{e.note}”</div>}
              </li>
            ))}
            {!real.length && <li className="text-xs text-slate-500">No current entries.</li>}
          </ol>
          {(audit.length > 0 || real.length > 0) && (
            <details className="text-[11px] text-slate-500" data-testid={`${testid}-audit`}>
              <summary className="cursor-pointer">Correction & recording audit ({audit.length})</summary>
              <ul className="mt-1 space-y-1 pl-3">
                {audit.map((e) => <li key={e.id}>{auditLabel(e, kind)} — recorded {fmtDateTime(e.recorded_at)} by {e.recorded_by_name || "—"}</li>)}
                {real.map((e) => <li key={`r-${e.id}`}>{(LABELS[kind]?.[e.type] || e.type)} entered {fmtDateTime(e.recorded_at)} by {e.recorded_by_name || "—"}</li>)}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
